import QRCode from 'qrcode';

/**
 * The Rewards Card's two barcodes, drawn from the server's payload only
 * (`JT1:<card number>`; the number alone in Code 128). Nothing about the
 * person is ever encoded (DPIA addendum §3).
 *
 * - QR: phone cameras and modern counter scanners (D2 default).
 * - Code 128, subset C: older laser counter scanners. Sixteen digits pack into
 *   eight symbols. The pattern table is ZXing's own (`Code128Reader.CODE_PATTERNS`,
 *   zxing core 3.5.3), copied rather than retyped; the test decodes what this
 *   draws with ZXing.
 */

/** Bar/space widths (in modules), bar first, for Code 128 values 0–106 (103–105 start A/B/C, 106 stop). */
const CODE128: readonly string[] = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
  '2331112',
];
const START_C = 105;
const STOP = 106;
/** Clear space either side of a linear barcode, in modules (ISO/IEC 15417 asks for at least 10). */
export const QUIET_MODULES = 10;

export interface Bars {
  /** Total width in modules, quiet zones included. */
  readonly modules: number;
  /** Each dark bar: its x offset and width, in modules. */
  readonly bars: readonly { readonly x: number; readonly w: number }[];
}

/** Code 128 subset C for an even number of digits (a card number). */
export function code128c(digits: string): Bars {
  if (!/^(\d\d)+$/u.test(digits)) throw new Error('Code 128 subset C needs an even number of digits.');
  const values = [START_C];
  for (let i = 0; i < digits.length; i += 2) values.push(Number(digits.slice(i, i + 2)));
  const check = values.reduce((sum, value, index) => sum + value * (index === 0 ? 1 : index), 0) % 103;
  values.push(check, STOP);
  const bars: { x: number; w: number }[] = [];
  let x = QUIET_MODULES;
  for (const value of values) {
    const widths = CODE128[value] ?? '';
    for (let i = 0; i < widths.length; i += 1) {
      const w = Number(widths[i]);
      if (i % 2 === 0) bars.push({ x, w });
      x += w;
    }
  }
  return { modules: x + QUIET_MODULES, bars };
}

/** The QR code's modules (true = dark), row by row, with error correction M. */
export function qrModules(text: string): {
  readonly size: number;
  readonly dark: (row: number, col: number) => boolean;
} {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const { size, data } = qr.modules;
  return { size, dark: (row, col) => data[row * size + col] === 1 };
}
